import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchLoyaltyStatus } from '../../api/loyalitystatus';
import { logOut } from '../../../redux/auth/authSlice';

// Async thunk for fetching loyalty status
export const fetchLoyaltyStatusAsync = createAsyncThunk(
  'loyaltyStatus/fetchLoyaltyStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchLoyaltyStatus();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch loyalty status');
    }
  }
);

export interface LoyaltyStatus {
  loyaltyData: {
    loyaltyTierId: number;
    loyaltyTierName: string;
    pointsEarned: number;
    pointsRequired: number;
    discountPercentage: number;
    hasReviewedOnGoogle: boolean;
    hasReviewedOnTrustPilot: boolean;
    loyaltyType: number;
  } | null;
  loading: boolean;
  error: string | null;
  message: string | null;
}

const initialState: LoyaltyStatus = {
  loyaltyData: null,
  loading: false,
  error: null,
  message: null,
};

const loyaltyStatusSlice = createSlice({
  name: 'loyaltyStatus',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    clearLoyaltyData: (state) => {
      state.loyaltyData = null;
      state.error = null;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLoyaltyStatusAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(fetchLoyaltyStatusAsync.fulfilled, (state, action: PayloadAction<LoyaltyStatus['loyaltyData']>) => {
        state.loading = false;
        state.loyaltyData = action.payload;
        state.message = 'Loyalty status fetched successfully';
        state.error = null;
      })
      .addCase(fetchLoyaltyStatusAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'An unknown error occurred';
        state.loyaltyData = null;
        state.message = null;
      })
      // Reset state on logout
      .addCase(logOut, () => initialState);
  },
});

export const { clearError, clearMessage, clearLoyaltyData } = loyaltyStatusSlice.actions;

export default loyaltyStatusSlice.reducer;